import {
  formatElapsed,
  type WorkspaceRunTimer
} from '~/components/workspaces/hooks/use-workspace-run-timer';

export const InProgressIndicator = () => (
  <div className="relative size-2.5">
    <svg
      viewBox="0 0 10 10"
      aria-hidden="true"
      className="size-2.5 absolute top-0 left-0 z-0 animate-ping"
    >
      <circle r={5} cx={5} cy={5} className="fill-blue-300 opacity-95" />
    </svg>
    <svg viewBox="0 0 10 10" aria-hidden="true" className="size-2.5 absolute top-0 left-0">
      <circle r={4} cx={5} cy={5} className="fill-blue-400" />
    </svg>
  </div>
);

export const CompletedIndicator = () => (
  <div className="relative size-2.5">
    <svg viewBox="0 0 10 10" aria-hidden="true" className="size-2.5 absolute top-0 left-0 z-0">
      <circle r={5} cx={5} cy={5} className="fill-green-300" />
    </svg>
  </div>
);

/**
 * Status display for the right-aligned cluster of the top bar. Renders one of
 * three states based on the workspace run timer:
 *
 * - Working: pulsing blue dot, "Working", and live-ticking elapsed time.
 * - Done: solid dot, "Idle", and "Took {duration}".
 * - Never run: nothing — silence keeps the bar quiet for fresh workspaces.
 *
 * Visual indicators are reused from `conversation-stage-event-thought.tsx` to
 * keep the agent's "running" pulse consistent across the page.
 */
export const WorkspaceTopBarStatus = (props: { timer: WorkspaceRunTimer }) => {
  const { timer } = props;

  if (timer.status === 'never_run' || timer.elapsedMs == null) return null;

  const formatted = formatElapsed(timer.elapsedMs);

  if (timer.status === 'working') {
    return (
      <div
        className="flex flex-row items-center gap-1.5 text-xs min-w-0"
        role="status"
        aria-live="polite"
      >
        <span className="shrink-0">
          <InProgressIndicator />
        </span>
        <span className="font-medium text-neutral-700 dark:text-neutral-300 truncate min-w-0 max-w-48">
          {timer.currentStep ?? 'Working'}
        </span>
        <span className="text-neutral-300 dark:text-neutral-600 shrink-0" aria-hidden="true">
          ·
        </span>
        <span className="tabular-nums text-neutral-500 dark:text-neutral-400 shrink-0">
          {formatted}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center gap-1.5 text-xs" role="status" aria-live="polite">
      <CompletedIndicator />
      <span className="font-medium text-neutral-700 dark:text-neutral-300">Idle</span>
      <span className="text-neutral-300 dark:text-neutral-600" aria-hidden="true">
        ·
      </span>
      <span className="tabular-nums text-neutral-500 dark:text-neutral-400">Took {formatted}</span>
    </div>
  );
};
